import { type RefObject, useLayoutEffect, useState } from 'react'

export interface TabRowMetrics {
  /** Intrinsic inline size of each tab, in source order. */
  widths: number[]
  gapWidthPx: number
  /** Constant across counts: the trigger reserves its widest label up front. */
  moreWidth: number
  available: number
  /** Always keeps a slot. -1 when nothing is active. */
  activeIndex: number
}

const indexRange = (length: number): number[] => Array.from({ length }, (_unused, index) => index)

/** Measure tab size with fractional pixels. `offsetWidth` rounds & with many tabs the accumulation is significant. */
const inlineSize = (element: Element): number => element.getBoundingClientRect().width

/** Resolved styles are already in pixels; keywords like `normal` fall back to zero. */
const inPx = (value: string): number => Number.parseFloat(value) || 0

/** `clientWidth` is the padding box, so the platform has already taken any border off. */
const contentInlineSize = (element: HTMLElement): number => {
  const { paddingInlineStart, paddingInlineEnd } = getComputedStyle(element)
  return element.clientWidth - inPx(paddingInlineStart) - inPx(paddingInlineEnd)
}

/** The active tab claims the last slot when it falls outside the leading prefix.*/
export const visibleTabIndices = ({
  total,
  count,
  activeIndex,
}: {
  total: number
  count: number
  activeIndex: number
}): number[] => {
  const slots = Math.min(Math.max(count, 0), total)
  if (slots === 0 || activeIndex < 0 || activeIndex < slots) {
    return indexRange(slots)
  }
  return [...indexRange(slots - 1), activeIndex]
}

/** How many tabs keep an in-flow slot; the rest stow into the fly-out. */
export const computeVisibleCount = ({
  widths,
  gapWidthPx,
  moreWidth,
  available,
  activeIndex,
}: TabRowMetrics): number => {
  const total = widths.length
  // A row of two can't trim: "never 1 more" would stow both and leave nothing but the trigger.
  if (total <= 2) {
    return total
  }

  const rowWidth = (indices: number[]): number =>
    indices.reduce((sum, index) => sum + (widths[index] ?? 0), 0) +
    Math.max(indices.length - 1, 0) * gapWidthPx

  // Everything fits: nothing stows and no trigger slot is reserved, whatever the trigger costs.
  if (rowWidth(indexRange(total)) <= available) {
    return total
  }

  // The trigger takes the slot after the last visible tab: its own width plus one gap.
  const fits = (count: number): boolean =>
    rowWidth(visibleTabIndices({ activeIndex, count, total })) + gapWidthPx + moreWidth <= available

  // Widest row that still fits. Candidates stop at 1: reaching 0 would stow the active tab.
  const count = indexRange(total).slice(1).findLast(fits) ?? 1

  // Never "1 more": the full row already didn't fit, so that last tab would clip. Stowing a second
  // is strictly narrower than the case that just fit.
  return count === total - 1 ? total - 2 : count
}

export interface TabOverflowOptions {
  /** Unique id per tab, in source order — whatever the host already uses as its React key. */
  ids: readonly string[]
  activeKey: string
}

/**
 * Measures a set of tabs and reports which overflow and so should be stowed by the component.
 * The active tab is always kept visible & at least 2 are stowed (since a new tab is created to view the overflow meny)
 */
export const useTabOverflow = (
  rowRef: RefObject<HTMLElement | null>,
  { ids, activeKey }: TabOverflowOptions,
): ReadonlySet<string> => {
  const [visibleCount, setVisibleCount] = useState(ids.length)
  // Read from the DOM below, because NavLink owns route matching and only reports it as a class.
  const [activeIndex, setActiveIndex] = useState(-1)

  // The dependency array needs a primitive; the mutation observer covers what a join could blur.
  const rowKey = ids.join(' ')

  // Measure the row and decide how many tabs keep a slot, before paint so the trim never shows.
  useLayoutEffect(() => {
    const row = rowRef.current
    const more = row?.querySelector<HTMLElement>('.tab.more')
    if (!row || !more) {
      return
    }

    const measure = (): void => {
      // Descendant, not child: Tooltip wraps a disabled tab in a `display: contents` host.
      const tabs = [...row.querySelectorAll<HTMLElement>('.tab:not(.more)')]
      const active = tabs.findIndex((tab) => tab.classList.contains('active'))

      setActiveIndex(active)
      setVisibleCount(
        computeVisibleCount({
          activeIndex: active,
          available: contentInlineSize(row),
          // Off the row itself, so a gap named in a token can't drift from the one CSS applies.
          gapWidthPx: inPx(getComputedStyle(row).columnGap),
          moreWidth: inlineSize(more),
          widths: tabs.map(inlineSize),
        }),
      )
    }

    measure()

    // The row for the budget, each tab for its own width — a tab grows without the row moving when
    // a label lengthens, a count gains a digit, or a webfont lands.
    const resizeObserver = new ResizeObserver(measure)
    const observeRow = (): void => {
      resizeObserver.observe(row)
      for (const tab of row.querySelectorAll<HTMLElement>('.tab')) {
        resizeObserver.observe(tab)
      }
    }
    observeRow()

    // Toggling `disabledMessage` swaps a tab's node without changing `ids`, stranding the resize
    // observer on a detached one. Re-observing is all this is for.
    const childObserver = new MutationObserver(() => {
      observeRow()
      measure()
    })
    childObserver.observe(row, { childList: true, subtree: true })

    return () => {
      resizeObserver.disconnect()
      childObserver.disconnect()
    }
  }, [rowRef, rowKey, activeKey])

  const visible = new Set(
    visibleTabIndices({ activeIndex, count: visibleCount, total: ids.length }),
  )
  return new Set(ids.filter((_id, index) => !visible.has(index)))
}
